import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { ArrowRight, ChefHat, Truck, Gavel, ShieldCheck, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const HERO = "https://images.unsplash.com/photo-1541557435984-1c79685a082b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxmcmVzaCUyMHJlc3RhdXJhbnQlMjBmb29kJTIwaW5ncmVkaWVudHMlMjBjaGVmJTIwc3VwcGxpZXJ8ZW58MHx8fHwxNzkwNDEwMjYxfDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => setStats({ open_auctions: 0, suppliers: 0, deliveries: 0 })); }, []);
  return (
    <div>
      {/* Sticky hero: logo + tasti sotto, restano visibili anche in scroll */}
      <div className="sticky top-0 z-40 bg-[#FAF6EC] border-b border-slate-200 shadow-sm" data-testid="landing-hero-banner">
        <img src="/logo-h.jpg?v=5" alt="AstaAlimentare · Marketplace B2B" className="w-full h-auto max-h-40 sm:max-h-48 lg:max-h-56 object-contain select-none" draggable="false" />
        <div className="flex items-center justify-center gap-3 sm:gap-4 py-3 sm:py-4 border-t border-slate-100">
          <Link to="/login">
            <Button variant="outline" className="rounded-full border-slate-300 shadow-sm hover:bg-slate-50" data-testid="hero-login-button">
              Accedi
            </Button>
          </Link>
          <Link to="/registrazione">
            <Button className="ga-btn-primary rounded-full shadow-sm" data-testid="hero-register-button">
              Registrati
            </Button>
          </Link>
        </div>
      </div>

      <section className="relative overflow-hidden ga-hero-grain">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Badge className="mb-5 rounded-full bg-white text-slate-800 border border-slate-200 hover:bg-white" data-testid="hero-badge">
              <Gavel className="w-3.5 h-3.5 mr-1.5" /> Asta inversa · vince il prezzo più basso
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Il tuo <span style={{color: "var(--ga-primary)"}}>ordine</span>, la loro <span style={{color: "var(--ga-emerald)"}}>miglior offerta</span>.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
              AstaAlimentare è il marketplace B2B in cui gli acquirenti pubblicano ordini merce e i fornitori italiani si sfidano al ribasso in tempo reale.
              Migliori prezzi, pagamenti protetti, punteggi reciproci: la filiera trasparente per la ristorazione.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/registrazione?role=restaurant">
                <Button size="lg" className="ga-btn-primary rounded-full w-full sm:w-auto" data-testid="cta-restaurant-button">
                  <ChefHat className="w-4 h-4 mr-2" /> Sono un Acquirente
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/registrazione?role=supplier">
                <Button size="lg" variant="outline" className="rounded-full border-slate-300 w-full sm:w-auto" data-testid="cta-supplier-button">
                  <Truck className="w-4 h-4 mr-2" /> Sono un Fornitore
                </Button>
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-6 max-w-lg" data-testid="hero-live-stats">
              <Stat n={stats ? stats.open_auctions : "…"} l="Aste attive ora" testId="stat-open-auctions" />
              <Stat n={stats ? stats.suppliers : "…"} l="Fornitori iscritti" testId="stat-suppliers" />
              <Stat n={stats ? stats.deliveries : "…"} l="Consegne completate" testId="stat-deliveries" />
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <img src={HERO} alt="Acquirente e fornitori" className="rounded-3xl shadow-xl object-cover w-full h-[420px] lg:h-[520px]" />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RoleCard
            testId="role-card-restaurant"
            role="Acquirente"
            title="Ordini merce senza chiamate né trattative infinite"
            icon={<ChefHat className="w-6 h-6" />}
            color="var(--ga-primary)"
            bullets={[
              "Crea un ordine per categoria in 60 secondi",
              "I fornitori competono al ribasso entro la scadenza",
              "Scegli manualmente l'offerta migliore o lascia vincere la più bassa",
              "Pagamenti protetti (carta o bonifico) e valutazione fornitore integrata",
            ]}
            cta={{ to: "/registrazione?role=restaurant", label: "Registrati come Acquirente" }}
          />
          <RoleCard
            testId="role-card-supplier"
            role="Fornitore"
            title="Nuovi clienti ogni giorno, senza rappresentanti"
            icon={<Truck className="w-6 h-6" />}
            color="var(--ga-emerald)"
            bullets={[
              "Ricevi solo ordini nella tua categoria",
              "Fai la tua offerta con prezzo e tempi consegna",
              "Vinci l'asta e organizza la consegna in chat",
              "Costruisci una reputazione con recensioni verificate",
            ]}
            cta={{ to: "/registrazione?role=supplier", label: "Registrati come Fornitore" }}
          />
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Come funziona</h2>
          <p className="mt-2 text-slate-600 max-w-2xl">Tre passaggi: pubblichi, ricevi offerte, scegli o lascia decidere l'asta.</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Step n="01" title="L'acquirente pubblica" body="Categoria, articoli, indirizzo consegna e durata asta (max 12h)." />
            <Step n="02" title="I fornitori offrono" body="Tutti i fornitori iscritti alla categoria ricevono l'ordine e possono offrire il proprio prezzo." />
            <Step n="03" title="Aggiudicazione" body="Alla scadenza vince l'offerta più bassa, oppure l'acquirente può scegliere manualmente." />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16" id="faq">
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500">FAQ</div>
        <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Domande frequenti</h2>
        <p className="mt-2 text-slate-600 max-w-2xl">Tutto quello che vuoi sapere prima di iscriverti.</p>
        <div className="mt-8 space-y-3">
          <FaqItem q="Chi paga la commissione?" a="La piattaforma trattiene una piccola commissione dal fornitore vincitore. L'acquirente non paga fee: riceve la merce al prezzo esatto dell'aggiudicazione." />
          <FaqItem q="Quanto è la commissione?" a="6% sul prezzo di aggiudicazione per fornitori Free (minimo €1,50 per ordine). Con piano a pagamento: Basic €29,99/mese → 4%, Pro €99,99/mese → 2,5%, Enterprise €299/mese → 1%. Ai nuovi fornitori i primi 45 giorni al 2% (onboarding agevolato). Per l'acquirente la piattaforma è gratuita." />
          <FaqItem q="Come funziona il punteggio fedeltà?" a="Il badge 5★ Fedeltà è pensato per i fornitori: si sblocca con rating medio ≥ 4,8 e almeno 5 consegne nella finestra scorrevole degli ultimi 6 mesi. Chi ce l'ha ottiene uno sconto -20% sulla commissione della piattaforma. Il calcolo è aggiornato ogni mese." />
          <FaqItem q="Gli acquirenti hanno un punteggio?" a="Sì: il Punteggio Pagamenti (Nuovo, Basso, Discreto, Buono, Ottimo, Eccellente) è calcolato sul tasso di pagamento e sulla velocità media dall'aggiudicazione. I fornitori lo vedono prima di offrire — spinge gli acquirenti a pagare in fretta." />
          <FaqItem q="Come funziona l'asta inversa?" a="L'acquirente pubblica l'ordine con durata (max 12h). Tutti i fornitori iscritti a quella categoria ricevono la notifica e possono offrire. Alla scadenza vince l'offerta più bassa (o l'acquirente sceglie manualmente). Zero telefonate, zero trattative." />
          <FaqItem q="Fatturazione elettronica SdI?" a="Sì. La piattaforma genera automaticamente l'XML in formato FPR12 e lo trasmette a SdI tramite Fatture in Cloud o Aruba (attivabili con proprie credenziali)." />
          <FaqItem q="Metodi di pagamento?" a="Carta di credito/debito (Visa, Mastercard) tramite Stripe con importo bloccato e addebitato solo alla conferma della consegna, oppure bonifico bancario istantaneo sull'IBAN del fornitore con conferma di ricezione. PayPal in fase di attivazione. Gli abbonamenti fornitore (Basic, Pro, Enterprise) sono gestiti via Stripe." />
          <FaqItem q="Posso cancellare l'abbonamento Premium?" a="Sì, in qualsiasi momento dal pulsante 'Gestisci abbonamento' nel tuo dashboard (portale Stripe ufficiale). L'accesso Premium resta attivo fino alla fine del periodo pagato." />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Feature icon={<Gavel className="w-5 h-5" />} title="Aste inverse trasparenti" body="Ogni offerta è visibile in ordine di prezzo. Zero conflitti, zero telefonate." />
          <Feature icon={<ShieldCheck className="w-5 h-5" />} title="Pagamenti protetti" body="Carta (Visa, Mastercard) con importo bloccato e addebito solo a consegna confermata, o bonifico istantaneo con conferma del fornitore. Contestazioni gestite con rimborso automatico." />
          <Feature icon={<Star className="w-5 h-5" />} title="Doppia reputazione" body="I fornitori sono valutati sul servizio, gli acquirenti sulla puntualità dei pagamenti." />
        </div>
      </section>
    </div>
  );
}

function FaqItem({ q, a }) {
  return (
    <details className="ga-card p-5 group" data-testid="faq-item">
      <summary className="font-display font-bold text-slate-900 cursor-pointer flex items-center justify-between list-none">
        <span>{q}</span>
        <span className="ml-4 text-slate-400 group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
      </summary>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed">{a}</p>
    </details>
  );
}

function Stat({ n, l, testId }) {
  return (
    <div data-testid={testId}>
      <div className="font-display text-3xl font-extrabold text-slate-900 tabular-nums">{n}</div>
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mt-1">{l}</div>
    </div>
  );
}

function RoleCard({ role, title, bullets, cta, icon, color, testId }) {
  return (
    <div className="ga-card p-8" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white" style={{background: color}}>{icon}</div>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Per {role}</div>
      </div>
      <h3 className="mt-4 font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{title}</h3>
      <ul className="mt-5 space-y-2.5">
        {bullets.map((b, i) => (
          <li key={`${title}-${i}-${b.slice(0, 20)}`} className="flex items-start gap-3 text-slate-700 text-sm sm:text-base">
            <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{background: color}} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6">
        <Link to={cta.to}>
          <Button className="rounded-full ga-btn-primary" data-testid={`${testId}-cta`}>
            {cta.label} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="p-6 rounded-2xl bg-white border border-slate-200">
      <div className="font-mono text-sm text-slate-400">{n}</div>
      <div className="mt-2 font-display text-xl font-bold text-slate-900">{title}</div>
      <div className="mt-2 text-slate-600 text-sm leading-relaxed">{body}</div>
    </div>
  );
}

function Feature({ icon, title, body }) {
  return (
    <div className="p-6 rounded-2xl border border-slate-200 bg-white ga-card">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white mb-4" style={{background: "var(--ga-ink)"}}>{icon}</div>
      <div className="font-display text-lg font-bold text-slate-900">{title}</div>
      <div className="mt-2 text-sm text-slate-600">{body}</div>
    </div>
  );
}
